import { create } from "zustand";
import { api } from "../lib/api";

export interface RubricItem {
  criterion: string;
  weight: string | null;
  description: string;
}

export interface FeedbackEntry {
  pattern: string;
  frequency: string;
  recommendation: string;
}

export interface MaterialSummary {
  filename: string;
  type: string;
  summary: string;
}

export interface ContextModel {
  courseInfo: {
    name: string;
    instructor: string;
    term: string;
  } | null;
  rubric: RubricItem[];
  pastFeedback: FeedbackEntry[];
  materials: MaterialSummary[];
  assignmentRequirements: string;
  assignmentType: string;
  assignmentTitle: string;
}

export type SuggestionType =
  | "rubric_gap"
  | "feedback_pattern"
  | "citation_needed"
  | "requirement_missing"
  | "style_warning";

export type SuggestionSeverity = "info" | "warning" | "critical";

export interface Suggestion {
  id: string;
  type: SuggestionType;
  severity: SuggestionSeverity;
  message: string;
  section: string | null;
  action: { label: string; payload: unknown } | null;
}

interface ContextState {
  contextModel: ContextModel | null;
  suggestions: Suggestion[];
  dismissedIds: Set<string>;
  isBuilding: boolean;
  isLoadingSuggestions: boolean;
  lastBuilt: string | null;
  error: string | null;

  buildContext: (assignmentId: string) => Promise<void>;
  refreshSuggestions: (assignmentId: string, documentContent: string) => Promise<void>;
  dismissSuggestion: (id: string) => void;
  restoreSuggestion: (id: string) => void;
  clearContext: () => void;
  getVisibleSuggestions: () => Suggestion[];
}

export const useContextStore = create<ContextState>()((set, get) => ({
  contextModel: null,
  suggestions: [],
  dismissedIds: new Set(),
  isBuilding: false,
  isLoadingSuggestions: false,
  lastBuilt: null,
  error: null,

  buildContext: async (assignmentId) => {
    set({ isBuilding: true, error: null });

    try {
      const model = await api.post<ContextModel>("/api/v1/context/build", {
        assignment_id: assignmentId,
      });

      set({
        contextModel: model,
        isBuilding: false,
        lastBuilt: new Date().toISOString(),
      });
    } catch (err) {
      set({
        isBuilding: false,
        error: err instanceof Error ? err.message : "Failed to build context",
      });
    }
  },

  refreshSuggestions: async (assignmentId, documentContent) => {
    set({ isLoadingSuggestions: true });

    try {
      const resp = await api.post<{ suggestions: Suggestion[] }>(
        "/api/v1/context/suggestions",
        { assignment_id: assignmentId, document_content: documentContent },
      );

      set({
        suggestions: resp.suggestions,
        isLoadingSuggestions: false,
      });
    } catch {
      set({ isLoadingSuggestions: false });
    }
  },

  dismissSuggestion: (id) =>
    set((s) => {
      const next = new Set(s.dismissedIds);
      next.add(id);
      return { dismissedIds: next };
    }),

  restoreSuggestion: (id) =>
    set((s) => {
      const next = new Set(s.dismissedIds);
      next.delete(id);
      return { dismissedIds: next };
    }),

  clearContext: () =>
    set({
      contextModel: null,
      suggestions: [],
      dismissedIds: new Set(),
      lastBuilt: null,
      error: null,
    }),

  getVisibleSuggestions: () => {
    const { suggestions, dismissedIds } = get();
    return suggestions.filter((s) => !dismissedIds.has(s.id));
  },
}));
